import type { TAdvertisingCampain } from './models';

const MONTHS = 12;
const defaultAdvertisingCampaign: TAdvertisingCampain[] = [
    {
        baseValue: 14500,
        minLimit: 0,
        maxLimit: 10000000,
        costMultiplier: null,
    },
    {
        baseValue: 30000,
        minLimit: 10000000,
        maxLimit: 20000000,
        costMultiplier: null,
    },
    {
        baseValue: 26000,
        minLimit: 20000000,
        maxLimit: 4000000000,
        costMultiplier: 0.005,
    },
];

const getSubsidy = (itemCost: number, subsidyPercent: number, subsidyMax: number) => {
    let subsidy = (itemCost / 100) * subsidyPercent;

    if (subsidy > subsidyMax) {
        subsidy = subsidyMax;
    }

    return subsidy;
};

const checkFallbackCampaign = (campaign?: TAdvertisingCampain[]) => {
    if (!campaign || typeof campaign !== 'object') {
        return defaultAdvertisingCampaign;
    }

    return campaign;
};

export const getAdvertisingCampaign = (
    sumInvestment: number,
    campaign?: TAdvertisingCampain[]
): number => {
    const advCampaign = checkFallbackCampaign(campaign);

    const config =
        advCampaign.find(
            ({ maxLimit, minLimit }) => minLimit <= sumInvestment && sumInvestment < maxLimit
        ) ?? advCampaign[advCampaign.length - 1];

    let result = config.baseValue;

    if (config.costMultiplier) {
        result += config.costMultiplier * sumInvestment;
    }

    return result;
};

export const calcLease = ({
    advertisingCampaign: customAdvertisingCampaign,
    annualRate,
    buyBack,
    initialFee,
    itemCost,
    leaseTerm,
    subsidyMax,
    subsidyPercent,
    trackingSystem,
    vatRate,
}: {
    advertisingCampaign?: TAdvertisingCampain[];
    annualRate: number;
    buyBack: number;
    initialFee: number;
    itemCost: number;
    leaseTerm: number;
    subsidyMax?: number;
    subsidyPercent?: number;
    trackingSystem: number;
    vatRate: number;
}) => {
    const vatRateCoefficient = 1 + vatRate / 100;
    const annualRateCoefficient = annualRate / 100;
    const subsidy = getSubsidy(itemCost, subsidyPercent as number, subsidyMax as number);
    const firstPay = initialFee + subsidy;
    const advertisingCampaign = getAdvertisingCampaign(
        itemCost - firstPay,
        customAdvertisingCampaign
    );
    const monthlyPayment =
        ((annualRateCoefficient * vatRateCoefficient) /
            MONTHS /
            (1 - (1 + (annualRateCoefficient * vatRateCoefficient) / MONTHS) ** -leaseTerm)) *
            (itemCost +
                trackingSystem +
                advertisingCampaign * vatRateCoefficient -
                firstPay -
                buyBack) +
        (buyBack * annualRateCoefficient * vatRateCoefficient) / MONTHS;
    const leaseAmount = Math.round(firstPay + monthlyPayment * leaseTerm + buyBack);
    const taxSavings = Math.round((leaseAmount / 120) * 40);
    const acquisitionCosts = leaseAmount - taxSavings;

    return {
        monthlyPayment: Math.round(monthlyPayment),
        leaseAmount,
        taxSavings,
        acquisitionCosts,
    };
};
